
import { scale } from '../../util/scale';

/**
 * True when we should prefer the image fallback because emoji often don't
 * render (e.g. Windows without Segoe UI Emoji, or missing fonts).
 * When false, we show the emoji; when true and imageSrc is set, we show the image.
 * Uses userAgent only (navigator.platform is deprecated).
 */
function shouldUseImageFallback(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Windows/.test(navigator.userAgent);
}

export interface EmojiWithFallbackProps {
  /** The emoji character to display when no image fallback is used. */
  emoji: string;
  /**
   * Optional image URL used as fallback when emoji glyphs don't display
   * (e.g. on some Windows systems). We prefer showing the emoji; the image
   * is only used when the platform typically fails to render emoji.
   */
  imageSrc?: string | null;
  /** Accessible label for screen readers. */
  ariaLabel?: string;
  /** Size in pixels (or responsive array). Applied to both emoji span and img. */
  size?: number | number[];
}

const containerStyle = () =>
  scale({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'auto',
    height: '10px',
  });

const emojiSpanStyle = (size: number | number[] | undefined) =>
  scale({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size ?? '1em',
    lineHeight: 1,
  });

const imgStyle = () =>
  scale({
    width: 'auto',
    height: '100%',
    objectFit: 'contain',
  });

/**
 * Renders an emoji, using the image only when the platform typically doesn't
 * display emoji (e.g. Windows). Otherwise the emoji is shown; the image is
 * the fallback when it wouldn't display.
 */
export default function EmojiWithFallback({
  emoji,
  imageSrc,
  ariaLabel,
  size,
}: EmojiWithFallbackProps) {
  const label = ariaLabel ?? emoji;
  const useImage = imageSrc && shouldUseImageFallback();

  if (useImage) {
    return (
      <span role="img" aria-label={label} css={containerStyle()}>
        <img src={imageSrc} alt="" aria-hidden css={imgStyle()} />
      </span>
    );
  }

  return (
    <span role="img" aria-label={label} css={emojiSpanStyle(size)}>
      {emoji}
    </span>
  );
}
